"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import Link from "next/link";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import WebSidebarNavItem from "./WebSidebarNavItem";
import WebSidebarExpandableGroup from "./WebSidebarExpandableGroup";
import type { SidebarSection } from "@/components/navigation/navTypes";
interface WebSidebarExpandableProps {
  readonly section: SidebarSection;
  readonly expanded: boolean;
  readonly onToggle: (e?: React.MouseEvent) => void;
  readonly collapsed: boolean;
  readonly pathname: string | null;
  readonly "data-section"?: string | undefined;
}
function WebSidebarExpandable(t0) {
  const $ = _c(42);
  const {
    section,
    expanded,
    onToggle,
    collapsed,
    pathname,
    "data-section": dataSection
  } = t0;
  let t1;
  if ($[0] !== pathname) {
    t1 = href => pathname === href;
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isActive = t1;
  let t2;
  if ($[2] !== isActive || $[3] !== section.subsections) {
    t2 = section.subsections?.find(sub => sub.items.some(item => isActive(item.href)))?.name ?? null;
    $[2] = isActive;
    $[3] = section.subsections;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeSubsection = t2;
  let t3;
  if ($[5] !== activeSubsection) {
    t3 = () => activeSubsection;
    $[5] = activeSubsection;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [expandedSubsection, setExpandedSubsection] = useState(t3);
  let t4;
  if ($[7] !== activeSubsection) {
    t4 = () => {
      setExpandedSubsection(activeSubsection);
    };
    $[7] = activeSubsection;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== activeSubsection || $[10] !== pathname) {
    t5 = [activeSubsection, pathname];
    $[9] = activeSubsection;
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (subsectionName, isNowExpanded) => {
      setExpandedSubsection(isNowExpanded ? subsectionName : null);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSubsectionToggle = t6;
  let t7;
  bb0: {
    if (section.items.some(item_0 => isActive(item_0.href))) {
      t7 = true;
      break bb0;
    }
    if (section.subsections?.some(sub_0 => sub_0.items.some(item_1 => isActive(item_1.href)))) {
      t7 = true;
      break bb0;
    }
    t7 = false;
  }
  const hasActiveItem = t7;
  const panelId = `section-${section.key}`;
  let t8;
  if ($[13] !== onToggle) {
    t8 = e => onToggle(e);
    $[13] = onToggle;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== collapsed || $[16] !== expanded) {
    t9 = !collapsed && <ChevronRightIcon className={`tw-h-4 tw-w-4 tw-shrink-0 tw-ml-auto tw-transition-transform tw-duration-200 motion-reduce:tw-transition-none ${expanded ? "tw-rotate-90" : ""}`} />;
    $[15] = collapsed;
    $[16] = expanded;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== collapsed || $[19] !== dataSection || $[20] !== expanded || $[21] !== hasActiveItem || $[22] !== panelId || $[23] !== section.icon || $[24] !== section.name || $[25] !== t8 || $[26] !== t9) {
    t10 = <WebSidebarNavItem onClick={t8} icon={section.icon} label={section.name} active={hasActiveItem} collapsed={collapsed} ariaExpanded={expanded} ariaControls={panelId} data-section={dataSection} rightSlot={t9} />;
    $[18] = collapsed;
    $[19] = dataSection;
    $[20] = expanded;
    $[21] = hasActiveItem;
    $[22] = panelId;
    $[23] = section.icon;
    $[24] = section.name;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== activeSubsection || $[29] !== collapsed || $[30] !== expanded || $[31] !== expandedSubsection || $[32] !== isActive || $[33] !== panelId || $[34] !== pathname || $[35] !== section.items || $[36] !== section.name || $[37] !== section.subsections) {
    t11 = !collapsed && <div className={`tw-grid tw-transition-[grid-template-rows] tw-duration-300 tw-ease-out motion-reduce:tw-transition-none ${expanded ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]"}`}><div className="tw-overflow-hidden"><div id={panelId} aria-label={`${section.name} items`} className="tw-relative tw-p-0 tw-m-0"><div className={`tw-absolute tw-left-7 tw-top-0 tw-bottom-0 tw-w-px tw-bg-iron-800 tw-transition-opacity tw-duration-300 motion-reduce:tw-transition-none ${expanded ? "tw-opacity-100" : "tw-opacity-0"}`} aria-hidden="true" /><ul className="tw-list-none tw-p-0 tw-m-0">{section.items.map(item_2 => {
              const active = isActive(item_2.href);
              return <li key={item_2.href} className="tw-m-0 tw-p-0"><Link href={item_2.href} className={`tw-w-[calc(100%-2.75rem)] tw-flex tw-items-center tw-no-underline tw-rounded-xl tw-border-none tw-transition-colors tw-duration-200 motion-reduce:tw-transition-none tw-cursor-pointer focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500 focus-visible:tw-ring-offset-2 tw-font-medium tw-justify-start tw-ml-[2.75rem] tw-pl-3 tw-pr-3 tw-h-11 tw-text-base tw-touch-action-manipulation ${active ? "tw-text-white tw-bg-iron-900 desktop-hover:hover:tw-text-white desktop-hover:hover:tw-bg-iron-900 active:tw-text-white" : "tw-text-iron-400 tw-bg-transparent desktop-hover:hover:tw-bg-transparent desktop-hover:hover:tw-text-white active:tw-text-white"}`} aria-current={active ? "page" : undefined}>{item_2.name}</Link></li>;
            })}</ul>{section.subsections?.map(subsection => {
            const isSubExpanded = expanded && (expandedSubsection === subsection.name || activeSubsection === subsection.name);
            return <WebSidebarExpandableGroup key={subsection.name} name={subsection.name} items={subsection.items} pathname={pathname} expanded={isSubExpanded} onToggle={isNowExpanded_0 => handleSubsectionToggle(subsection.name, isNowExpanded_0)} />;
          })}</div></div></div>;
    $[28] = activeSubsection;
    $[29] = collapsed;
    $[30] = expanded;
    $[31] = expandedSubsection;
    $[32] = isActive;
    $[33] = panelId;
    $[34] = pathname;
    $[35] = section.items;
    $[36] = section.name;
    $[37] = section.subsections;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t10 || $[40] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
}
export default React.memo(WebSidebarExpandable);